<template>
  <div class="crm-lead-page">

    <!-- TOPBAR -->
    <div class="crm-topbar">
      <button class="crm-back-btn" @click="voltarParaLeads">
        <feather-icon icon="ArrowLeftIcon" size="15" />
        <span>Leads</span>
      </button>
      <span class="crm-topbar__sep">/</span>
      <span class="crm-topbar__crumb">{{ detalhe ? (detalhe.nome || 'Lead') : '…' }}</span>
    </div>

    <!-- LOADING -->
    <div v-if="loading" class="crm-loading">
      <b-spinner variant="primary" />
      <p>Carregando dados do lead…</p>
    </div>

    <!-- ERRO -->
    <div v-else-if="!detalhe" class="crm-empty">
      <feather-icon icon="AlertCircleIcon" size="40" />
      <p>Lead não encontrado.</p>
      <button class="crm-back-btn" @click="voltarParaLeads">Voltar</button>
    </div>

    <!-- CONTEÚDO PRINCIPAL -->
    <template v-else>

      <!-- HERO HEADER -->
      <div class="crm-hero">
        <div class="crm-hero__avatar-ring" :style="getCompletionRingStyle()">
          <div class="crm-hero__avatar">{{ getInitials(detalhe.nome) }}</div>
        </div>
        <div class="crm-hero__info">
          <h1 class="crm-hero__name">{{ detalhe.nome || 'Lead sem nome' }}</h1>
          <div class="crm-hero__meta">
            <span v-if="detalhe.whatsapp" class="crm-hero__meta-item">
              <feather-icon icon="PhoneIcon" size="12" />{{ detalhe.whatsapp }}
            </span>
            <span v-if="detalhe.email" class="crm-hero__meta-item">
              <feather-icon icon="MailIcon" size="12" />{{ detalhe.email }}
            </span>
            <span class="crm-hero__meta-item">
              <feather-icon icon="ClockIcon" size="12" />{{ formatDate(detalhe.created_at) }}
            </span>
          </div>
        </div>
        <div class="crm-hero__badges">
          <span
            class="crm-completion-badge"
            :class="leadCompletionStats.completed >= leadCompletionStats.total ? 'crm-completion-badge--ok' : 'crm-completion-badge--pending'"
          >
            <feather-icon :icon="leadCompletionStats.completed >= leadCompletionStats.total ? 'CheckCircleIcon' : 'ClockIcon'" size="12" />
            {{ leadCompletionStats.completed >= leadCompletionStats.total ? 'Lead Completo' : 'Lead Incompleto' }}
            <strong>{{ leadCompletionStats.completed }}/{{ leadCompletionStats.total }}</strong>
          </span>
          <span class="crm-source-badge" :class="'crm-source-badge--' + detalhe.source">
            <span class="crm-source-badge__icon" v-html="sourceIcon(detalhe.source)" />
            {{ sourceLabel(detalhe.source) }}
          </span>
        </div>
      </div>

      <!-- PIPELINE STAGES -->
      <div class="crm-pipeline">
        <button
          v-for="s in pipelineStages"
          :key="s.key"
          class="crm-pipeline__step"
          :class="{
            'crm-pipeline__step--active': currentStage === s.key,
            'crm-pipeline__step--done': pipelineStageIndex(currentStage) > pipelineStageIndex(s.key),
          }"
          :style="{ '--stage-color': s.color }"
          @click="updateStage(s.key)"
        >
          <span class="crm-pipeline__dot"></span>
          <span class="crm-pipeline__label">{{ s.label }}</span>
        </button>
      </div>

      <!-- BODY: tabs + sidebar -->
      <div class="crm-body">

        <!-- ── SIDEBAR ── -->
        <aside class="crm-sidebar">

          <!-- Contato -->
          <div class="crm-card">
            <div class="crm-card__header">
              <feather-icon icon="UserIcon" size="14" class="crm-card__icon" />
              Contato
            </div>
            <div class="crm-card__body">
              <div v-if="detalhe.whatsapp" class="crm-info-row">
                <div class="crm-info-row__icon"><feather-icon icon="PhoneIcon" size="13" /></div>
                <div>
                  <div class="crm-info-row__label">WhatsApp</div>
                  <a :href="'https://wa.me/' + detalhe.whatsapp.replace(/\D/g,'')" target="_blank" class="crm-info-row__value crm-info-row__value--link">{{ detalhe.whatsapp }}</a>
                </div>
              </div>
              <div v-if="detalhe.email" class="crm-info-row">
                <div class="crm-info-row__icon"><feather-icon icon="MailIcon" size="13" /></div>
                <div>
                  <div class="crm-info-row__label">E-mail</div>
                  <a :href="'mailto:' + detalhe.email" class="crm-info-row__value crm-info-row__value--link">{{ detalhe.email }}</a>
                </div>
              </div>
              <div v-if="!detalhe.whatsapp && !detalhe.email" class="crm-info-row__empty">Sem dados de contato</div>
            </div>
          </div>

          <!-- Atribuição de campanha -->
          <div class="crm-card crm-card--utm">
            <div class="crm-card__header">
              <feather-icon icon="BarChart2Icon" size="14" class="crm-card__icon" />
              Atribuição de Campanha
            </div>
            <div class="crm-card__body">

              <!-- Badge de fonte principal -->
              <div class="crm-utm-source-wrap">
                <span class="crm-source-badge crm-source-badge--lg" :class="'crm-source-badge--' + detalhe.source">
                  <span class="crm-source-badge__icon" v-html="sourceIcon(detalhe.source)" />
                  {{ sourceLabel(detalhe.source) }}
                </span>
              </div>

              <template v-if="hasUtm">
                <div v-if="detalhe.utm_source" class="crm-utm-row">
                  <span class="crm-utm-row__key">utm_source</span>
                  <span class="crm-utm-row__val">{{ detalhe.utm_source }}</span>
                </div>
                <div v-if="detalhe.utm_medium" class="crm-utm-row">
                  <span class="crm-utm-row__key">utm_medium</span>
                  <span class="crm-utm-row__val">{{ detalhe.utm_medium }}</span>
                </div>
                <div v-if="detalhe.utm_campaign" class="crm-utm-row">
                  <span class="crm-utm-row__key">utm_campaign</span>
                  <span class="crm-utm-row__val">{{ detalhe.utm_campaign }}</span>
                </div>
                <div v-if="detalhe.utm_term" class="crm-utm-row">
                  <span class="crm-utm-row__key">utm_term</span>
                  <span class="crm-utm-row__val">{{ detalhe.utm_term }}</span>
                </div>
                <div v-if="detalhe.utm_content" class="crm-utm-row">
                  <span class="crm-utm-row__key">utm_content</span>
                  <span class="crm-utm-row__val">{{ detalhe.utm_content }}</span>
                </div>
                <div v-if="detalhe.gclid" class="crm-utm-row">
                  <span class="crm-utm-row__key">gclid</span>
                  <span class="crm-utm-row__val crm-utm-row__val--truncate">{{ detalhe.gclid }}</span>
                </div>
                <div v-if="detalhe.fbclid" class="crm-utm-row">
                  <span class="crm-utm-row__key">fbclid</span>
                  <span class="crm-utm-row__val crm-utm-row__val--truncate">{{ detalhe.fbclid }}</span>
                </div>
              </template>

              <div v-else class="crm-utm-empty">
                <feather-icon icon="InfoIcon" size="14" />
                <span>Acesso orgânico — sem parâmetros de campanha</span>
              </div>

              <!-- Página de captura -->
              <div v-if="detalhe.page" class="crm-utm-page">
                <span class="crm-utm-row__key">Página</span>
                <a :href="detalhe.page" target="_blank" class="crm-utm-row__val crm-utm-row__val--link">{{ truncateUrl(detalhe.page) }}</a>
              </div>
            </div>
          </div>

          <!-- Fluxo -->
          <div class="crm-card">
            <div class="crm-card__header">
              <feather-icon icon="GitMergeIcon" size="14" class="crm-card__icon" />
              Fluxo de captura
            </div>
            <div class="crm-card__body">
              <div class="crm-flow-card">
                <div class="crm-flow-card__icon-wrap">
                  <feather-icon icon="GitMergeIcon" size="18" />
                </div>
                <span class="crm-flow-card__name">{{ detalhe.name }}</span>
              </div>
            </div>
          </div>

        </aside>

        <!-- ── MAIN: TABS ── -->
        <main class="crm-main">

          <!-- Tab nav -->
          <div class="crm-tabs">
            <button
              class="crm-tab"
              :class="{ 'crm-tab--active': activeTab === 'dados' }"
              @click="activeTab = 'dados'"
            >
              <feather-icon icon="ListIcon" size="14" />
              Dados coletados
              <span class="crm-tab__count">{{ detalhe.campos ? detalhe.campos.length : 0 }}</span>
            </button>
            <button
              class="crm-tab"
              :class="{ 'crm-tab--active': activeTab === 'notas' }"
              @click="activeTab = 'notas'"
            >
              <feather-icon icon="FileTextIcon" size="14" />
              Anotações
              <span class="crm-tab__count">{{ detalhe.notes ? detalhe.notes.length : 0 }}</span>
            </button>
            <button
              class="crm-tab"
              :class="{ 'crm-tab--active': activeTab === 'atividades' }"
              @click="activeTab = 'atividades'; carregarAtividades()"
            >
              <feather-icon icon="CalendarIcon" size="14" />
              Atividades
              <span class="crm-tab__count">{{ atividades.length }}</span>
            </button>
          </div>

          <!-- TAB: DADOS COLETADOS -->
          <div v-if="activeTab === 'dados'" class="crm-tab-content">
            <div v-if="captureRequiredFields.length" class="crm-key-answers">
              <div class="crm-key-answers__header">
                <feather-icon icon="CheckSquareIcon" size="14" />
                Perguntas-chave respondidas
                <span class="crm-key-answers__count">
                  {{ keyAnsweredFields.length }}/{{ captureKeyFields.length }}
                </span>
              </div>
              <div class="crm-key-answers__list">
                <div
                  v-for="field in keyAnsweredFields"
                  :key="'ok-' + field.label"
                  class="crm-key-answers__item crm-key-answers__item--ok"
                >
                  <span class="crm-key-answers__label">{{ field.label }}</span>
                  <span class="crm-key-answers__value">{{ field.value }}</span>
                </div>
                <div
                  v-for="label in missingKeyFields"
                  :key="'missing-' + label"
                  class="crm-key-answers__item crm-key-answers__item--missing"
                >
                  <span class="crm-key-answers__label">{{ label }}</span>
                  <span class="crm-key-answers__value">Não respondido</span>
                </div>
              </div>
            </div>
            <div class="crm-fields-list">
              <div
                v-for="campo in sortedCampos"
                :key="campo.ordem_resp"
                class="crm-field-item"
              >
                <div class="crm-field-item__icon">
                  <feather-icon :icon="getFieldIcon(campo.label)" size="15" />
                </div>
                <div class="crm-field-item__content">
                  <div class="crm-field-item__label">{{ campo.label }}</div>
                  <div class="crm-field-item__value">{{ campo.value || '—' }}</div>
                </div>
              </div>
              <div v-if="!detalhe.campos || !detalhe.campos.length" class="crm-tab-empty">
                <feather-icon icon="InboxIcon" size="28" />
                <p>Nenhum dado coletado</p>
              </div>
            </div>
          </div>

          <!-- TAB: ANOTAÇÕES -->
          <div v-if="activeTab === 'notas'" class="crm-tab-content">

            <!-- Adicionar nota -->
            <div class="crm-note-form">
              <textarea
                v-model="novaNote"
                class="crm-note-input"
                placeholder="Escreva uma anotação sobre este lead…"
                rows="3"
              />
              <div class="crm-note-form__footer">
                <button
                  class="crm-btn crm-btn--primary"
                  :disabled="!novaNote.trim() || savingNote"
                  @click="salvarNota"
                >
                  <b-spinner v-if="savingNote" small />
                  <feather-icon v-else icon="PlusIcon" size="14" />
                  {{ savingNote ? 'Salvando…' : 'Adicionar nota' }}
                </button>
              </div>
            </div>

            <!-- Lista de notas -->
            <div class="crm-notes-list">
              <div
                v-for="nota in notasOrdenadas"
                :key="nota.id"
                class="crm-note-item"
                :class="{ 'crm-note-item--pinned': nota.is_pinned }"
              >
                <div class="crm-note-item__header">
                  <span v-if="nota.is_pinned" class="crm-note-pin">
                    <feather-icon icon="BookmarkIcon" size="11" /> fixada
                  </span>
                  <span class="crm-note-item__date">{{ formatDate(nota.created_at) }}</span>
                  <div class="crm-note-item__actions">
                    <button
                      class="crm-note-action"
                      :title="nota.is_pinned ? 'Desafixar' : 'Fixar nota'"
                      @click="togglePin(nota)"
                    >
                      <feather-icon :icon="nota.is_pinned ? 'BookmarkIcon' : 'BookmarkIcon'" size="13" />
                    </button>
                    <button
                      class="crm-note-action crm-note-action--delete"
                      title="Excluir nota"
                      @click="deletarNota(nota)"
                    >
                      <feather-icon icon="TrashIcon" size="13" />
                    </button>
                  </div>
                </div>
                <p class="crm-note-item__text">{{ nota.note }}</p>
              </div>

              <div v-if="!detalhe.notes || !detalhe.notes.length" class="crm-tab-empty">
                <feather-icon icon="FileTextIcon" size="28" />
                <p>Nenhuma anotação ainda</p>
                <span>Adicione notas para acompanhar este lead</span>
              </div>
            </div>

          </div>

          <!-- TAB: ATIVIDADES -->
          <div v-if="activeTab === 'atividades'" class="crm-tab-content">

            <!-- Formulário nova atividade -->
            <div class="act-form">
              <div class="act-form__types">
                <button
                  v-for="t in activityTypes"
                  :key="t.key"
                  class="act-type-btn"
                  :class="{ 'act-type-btn--active': newActivity.type === t.key }"
                  @click="newActivity.type = t.key"
                >
                  <span class="act-type-btn__icon">{{ t.icon }}</span>
                  <span class="act-type-btn__label">{{ t.label }}</span>
                </button>
              </div>
              <div class="act-form__row">
                <input
                  v-model="newActivity.detail"
                  class="act-form__input"
                  :placeholder="activityPlaceholder"
                  @keydown.enter="criarAtividade"
                />
                <input
                  v-model="newActivity.dueDate"
                  type="datetime-local"
                  class="act-form__date"
                />
                <button class="act-form__btn" @click="criarAtividade" :disabled="!newActivity.type">
                  <feather-icon icon="PlusIcon" size="14" class="mr-50" />
                  Agendar
                </button>
              </div>
            </div>

            <!-- Lista de atividades -->
            <div class="act-list">
              <!-- Pendentes -->
              <div v-if="atividadesPendentes.length" class="act-section">
                <h4 class="act-section__title">
                  <feather-icon icon="ClockIcon" size="14" class="mr-50" />
                  Pendentes
                  <span class="act-section__count">{{ atividadesPendentes.length }}</span>
                </h4>
                <div
                  v-for="act in atividadesPendentes"
                  :key="act.id"
                  class="act-card"
                  :class="{ 'act-card--overdue': isOverdue(act) }"
                >
                  <button class="act-card__check" @click="concluirAtividade(act)" title="Marcar como concluída">
                    <feather-icon icon="CircleIcon" size="16" />
                  </button>
                  <div class="act-card__body">
                    <div class="act-card__top">
                      <span class="act-card__type-icon">{{ getActivityIcon(act.action) }}</span>
                      <span class="act-card__type-label">{{ getActivityLabel(act.action) }}</span>
                      <span v-if="act.from_value" class="act-card__date">
                        <feather-icon icon="CalendarIcon" size="11" class="mr-25" />
                        {{ formatDateTime(act.from_value) }}
                      </span>
                      <span v-if="isOverdue(act)" class="act-card__overdue-badge">Atrasada</span>
                    </div>
                    <p v-if="act.detail" class="act-card__detail">{{ act.detail }}</p>
                  </div>
                  <button class="act-card__delete" @click="excluirAtividade(act)" title="Excluir">
                    <feather-icon icon="XIcon" size="13" />
                  </button>
                </div>
              </div>

              <!-- Concluídas -->
              <div v-if="atividadesConcluidas.length" class="act-section">
                <h4 class="act-section__title act-section__title--done">
                  <feather-icon icon="CheckCircleIcon" size="14" class="mr-50" />
                  Concluídas
                  <span class="act-section__count">{{ atividadesConcluidas.length }}</span>
                </h4>
                <div
                  v-for="act in atividadesConcluidas"
                  :key="act.id"
                  class="act-card act-card--done"
                >
                  <span class="act-card__check act-card__check--done">
                    <feather-icon icon="CheckCircleIcon" size="16" />
                  </span>
                  <div class="act-card__body">
                    <div class="act-card__top">
                      <span class="act-card__type-icon">{{ getActivityIcon(act.action) }}</span>
                      <span class="act-card__type-label">{{ getActivityLabel(act.action) }}</span>
                      <span v-if="act.from_value" class="act-card__date">{{ formatDateTime(act.from_value) }}</span>
                    </div>
                    <p v-if="act.detail" class="act-card__detail">{{ act.detail }}</p>
                  </div>
                </div>
              </div>

              <!-- Histórico (mudanças de stage) -->
              <div v-if="atividadesHistorico.length" class="act-section">
                <h4 class="act-section__title act-section__title--hist">
                  <feather-icon icon="GitCommitIcon" size="14" class="mr-50" />
                  Histórico
                </h4>
                <div
                  v-for="act in atividadesHistorico"
                  :key="act.id"
                  class="act-hist-item"
                >
                  <span class="act-hist-item__dot"></span>
                  <span class="act-hist-item__text">
                    Movido de <strong>{{ act.from_value }}</strong> para <strong>{{ act.to_value }}</strong>
                  </span>
                  <span class="act-hist-item__date">{{ formatDateTime(act.created_at) }}</span>
                </div>
              </div>

              <div v-if="!atividades.length" class="crm-tab-empty">
                <feather-icon icon="CalendarIcon" size="28" />
                <p>Nenhuma atividade ainda</p>
                <span>Agende ligações, calls, propostas e mais</span>
              </div>
            </div>

          </div>

        </main>
      </div>

    </template>

    <!-- Modal excluir nota -->
    <b-modal id="modal-del-nota" hide-header hide-footer centered size="sm" body-class="p-0">
      <div class="crm-delete-modal">
        <div class="crm-delete-modal__header">
          <feather-icon icon="AlertCircleIcon" size="24" />
        </div>
        <div class="crm-delete-modal__body">
          <h4>Excluir anotação?</h4>
          <p>Esta ação não pode ser desfeita.</p>
        </div>
        <div class="crm-delete-modal__footer">
          <button class="crm-btn crm-btn--ghost" @click="$bvModal.hide('modal-del-nota')">Cancelar</button>
          <button class="crm-btn crm-btn--danger" @click="confirmarDeleteNota">Excluir</button>
        </div>
      </div>
    </b-modal>

  </div>
</template>

<script>
import { BSpinner } from 'bootstrap-vue'
import { useApi } from '@/composables/useApi'

const apiBack = useApi()

export default {
  components: { BSpinner },

  data() {
    return {
      leadId: this.$route.params.id_lead,
      detalhe: null,
      loading: true,
      activeTab: 'dados',
      novaNote: '',
      savingNote: false,
      notaParaDeletar: null,
      atividades: [],
      newActivity: { type: '', detail: '', dueDate: '' },
      activityTypes: [
        { key: 'ligacao', label: 'Ligação', icon: '📞', placeholder: 'Ex: Ligar para confirmar interesse' },
        { key: 'call', label: 'Call / Reunião', icon: '💻', placeholder: 'Ex: Call de apresentação do produto' },
        { key: 'email', label: 'Email', icon: '📧', placeholder: 'Ex: Enviar proposta por email' },
        { key: 'proposta', label: 'Proposta', icon: '📄', placeholder: 'Ex: Elaborar proposta comercial' },
        { key: 'whatsapp', label: 'WhatsApp', icon: '💬', placeholder: 'Ex: Mandar follow-up no WhatsApp' },
        { key: 'tarefa', label: 'Tarefa', icon: '✅', placeholder: 'Ex: Preparar material de apoio' },
      ],
      currentStage: 'novo',
      pipelineStages: (() => {
        const all = [
          { key: 'novo', label: 'Novo', color: '#3B82F6' },
          { key: 'contato', label: 'Contato', color: '#8B5CF6' },
          { key: 'qualificado', label: 'Qualificado', color: '#F59E0B' },
          { key: 'proposta', label: 'Proposta', color: '#E86A10' },
          { key: 'ganho', label: 'Ganho', color: '#10B981' },
          { key: 'perdido', label: 'Perdido', color: '#EF4444' },
        ]
        let enabled
        try { enabled = JSON.parse(localStorage.getItem('pipeline_stages')) } catch {}
        if (enabled && Array.isArray(enabled)) return all.filter(s => enabled.includes(s.key))
        return all
      })(),
      captureFieldsConfig: [],
      captureRequiredFields: [],
    }
  },

  computed: {
    getUserData() {
      return JSON.parse(localStorage.getItem('userData') || '{}')
    },
    sortedCampos() {
      if (!this.detalhe || !this.detalhe.campos) return []
      return [...this.detalhe.campos].sort((a, b) => (a.ordem_resp || 0) - (b.ordem_resp || 0))
    },
    captureKeyFields() {
      const labels = (this.captureFieldsConfig || [])
        .map((f) => String(f && f.label ? f.label : '').trim())
        .filter(Boolean)
      return labels.length ? labels : ['Nome', 'Email', 'WhatsApp']
    },
    keyAnsweredFields() {
      if (!this.captureKeyFields.length) return []
      return this.captureKeyFields
        .map((label) => {
          const found = this.findCampoByRequiredLabel(label)
          return found && String(found.value || '').trim()
            ? { label, value: String(found.value || '').trim() }
            : null
        })
        .filter(Boolean)
    },
    missingKeyFields() {
      if (!this.captureKeyFields.length) return []
      return this.captureKeyFields.filter((label) => {
        const found = this.findCampoByRequiredLabel(label)
        return !found || !String(found.value || '').trim()
      })
    },
    leadCompletionStats() {
      const total = Math.max(this.captureRequiredFields.length, 1)
      const completed = this.keyAnsweredFields.length
      return { total, completed, pct: Math.round((completed / total) * 100) }
    },
    notasOrdenadas() {
      if (!this.detalhe || !this.detalhe.notes) return []
      return [...this.detalhe.notes].sort((a, b) => {
        if (a.is_pinned !== b.is_pinned) return b.is_pinned - a.is_pinned
        return new Date(b.created_at) - new Date(a.created_at)
      })
    },
    activityPlaceholder() {
      const t = this.activityTypes.find(a => a.key === this.newActivity.type)
      return t ? t.placeholder : 'Selecione o tipo de atividade acima'
    },
    atividadesPendentes() {
      return this.atividades.filter(a => a.action !== 'stage_changed' && a.to_value !== 'concluida')
    },
    atividadesConcluidas() {
      return this.atividades.filter(a => a.action !== 'stage_changed' && a.to_value === 'concluida')
    },
    atividadesHistorico() {
      return this.atividades.filter(a => a.action === 'stage_changed')
    },
    hasUtm() {
      if (!this.detalhe) return false
      return !!(this.detalhe.utm_source || this.detalhe.utm_medium || this.detalhe.utm_campaign ||
                this.detalhe.utm_term || this.detalhe.utm_content || this.detalhe.fbclid || this.detalhe.gclid)
    },
  },

  mounted() {
    this.carregarDetalhe()
  },

  methods: {
    voltarParaLeads() {
      const query = this.$route && this.$route.query ? this.$route.query : {}
      return this.$router.push({ name: 'leads', query })
    },
    async carregarDetalhe() {
      this.loading = true
      try {
        const resp = await apiBack.get(`lead-detail/${this.leadId}`)
        this.detalhe = resp.data
        await this.loadCaptureRequiredFields()
        // Carrega stage do pipeline
        this.carregarStage()
      } catch (e) {
        console.error(e)
        this.detalhe = null
      } finally {
        this.loading = false
      }
    },
    normalizeFieldLabel(label) {
      return String(label || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '')
    },
    findCampoByRequiredLabel(requiredLabel) {
      const target = this.normalizeFieldLabel(requiredLabel)
      const aliases = {
        nome: ['nome', 'name'],
        email: ['email', 'e-mail', 'mail'],
        whatsapp: ['whatsapp', 'telefone', 'celular', 'phone'],
      }
      // Fallback robusto: usa os campos normalizados já resolvidos no detalhe
      // quando o histórico de "campos" vier com label inconsistente.
      if (aliases.nome.includes(target) && String(this.detalhe?.nome || '').trim()) {
        return { label: requiredLabel, value: String(this.detalhe.nome).trim() }
      }
      if (aliases.email.includes(target) && String(this.detalhe?.email || '').trim()) {
        return { label: requiredLabel, value: String(this.detalhe.email).trim() }
      }
      if (aliases.whatsapp.includes(target) && String(this.detalhe?.whatsapp || '').trim()) {
        return { label: requiredLabel, value: String(this.detalhe.whatsapp).trim() }
      }

      const accepted = aliases[target] || [target]
      return this.sortedCampos.find((c) => accepted.includes(this.normalizeFieldLabel(c && c.label)))
    },
    getCompletionRingStyle() {
      const stats = this.leadCompletionStats
      const step = 360 / stats.total
      const gap = Math.min(6, step * 0.3)
      const fillColor = '#22c55e'
      const emptyColor = 'rgba(148, 163, 184, 0.42)'
      const sepColor = 'rgba(15, 23, 42, 0.45)'
      const segments = []
      for (let i = 0; i < stats.total; i++) {
        const start = i * step
        const segEnd = (i + 1) * step
        const fillEnd = Math.max(start, segEnd - gap)
        const color = i < stats.completed ? fillColor : emptyColor
        segments.push(`${color} ${start}deg ${fillEnd}deg`)
        segments.push(`${sepColor} ${fillEnd}deg ${segEnd}deg`)
      }
      return { background: `conic-gradient(${segments.join(',')})` }
    },
    async loadCaptureRequiredFields() {
      if (!this.detalhe || !this.detalhe.id_chat) {
        this.captureFieldsConfig = [
          { label: 'Nome', required: true },
          { label: 'Email', required: true },
          { label: 'WhatsApp', required: true },
        ]
        this.captureRequiredFields = ['Nome', 'Email', 'WhatsApp']
        return
      }
      try {
        const { data } = await apiBack.get(`chat-ai/${this.detalhe.id_chat}`)
        let capture = []
        try {
          capture = data && data.ai_capture_fields ? JSON.parse(data.ai_capture_fields) : []
        } catch {
          capture = []
        }
        const normalizedAll = (capture || [])
          .filter((f) => f && String(f.label || '').trim())
          .map((f) => ({ label: String(f.label).trim(), required: !!f.required }))
        this.captureFieldsConfig = normalizedAll.length ? normalizedAll : [
          { label: 'Nome', required: true },
          { label: 'Email', required: true },
          { label: 'WhatsApp', required: true },
        ]
        const required = capture
          .filter((f) => f && f.required && f.label)
          .map((f) => String(f.label).trim())
        this.captureRequiredFields = required.length ? required : ['Nome', 'Email', 'WhatsApp']
      } catch {
        this.captureFieldsConfig = [
          { label: 'Nome', required: true },
          { label: 'Email', required: true },
          { label: 'WhatsApp', required: true },
        ]
        this.captureRequiredFields = ['Nome', 'Email', 'WhatsApp']
      }
    },

    async carregarStage() {
      try {
        const ud = JSON.parse(localStorage.getItem('userData'))
        const { data } = await apiBack.get(`lead-pipeline/${ud.id}`)
        const lead = (data.leads || []).find(l => l.id_conversa === this.leadId)
        if (lead) this.currentStage = lead.stage || 'novo'
      } catch {}
    },

    async updateStage(stage) {
      const prev = this.currentStage
      this.currentStage = stage
      try {
        const ud = JSON.parse(localStorage.getItem('userData'))
        await apiBack.put(`lead-pipeline/${this.leadId}`, { stage, id_user: ud.id })
        this.$message({ type: 'success', message: `Lead movido para ${stage}` })
      } catch (e) {
        this.currentStage = prev
        this.$message({ type: 'error', message: 'Erro ao atualizar stage' })
      }
    },

    pipelineStageIndex(key) {
      return this.pipelineStages.findIndex(s => s.key === key)
    },

    // ── Atividades ──
    async carregarAtividades() {
      try {
        const { data } = await apiBack.get(`lead-activity/${this.leadId}`)
        this.atividades = Array.isArray(data) ? data : []
      } catch { this.atividades = [] }
    },
    async criarAtividade() {
      if (!this.newActivity.type) return
      try {
        const ud = JSON.parse(localStorage.getItem('userData'))
        await apiBack.post('lead-activity', {
          id_conversa: this.leadId,
          id_user: ud.id,
          action: this.newActivity.type,
          detail: this.newActivity.detail || null,
          due_date: this.newActivity.dueDate || null,
        })
        this.newActivity = { type: '', detail: '', dueDate: '' }
        this.carregarAtividades()
        this.$message({ type: 'success', message: 'Atividade agendada!' })
      } catch {
        this.$message({ type: 'error', message: 'Erro ao criar atividade' })
      }
    },
    async concluirAtividade(act) {
      try {
        await apiBack.put(`lead-activity/${act.id}`, { to_value: 'concluida' })
        this.carregarAtividades()
        this.$message({ type: 'success', message: 'Atividade concluída!' })
      } catch {}
    },
    async excluirAtividade(act) {
      try {
        await apiBack.delete(`lead-activity/${act.id}`)
        this.carregarAtividades()
      } catch {}
    },
    getActivityIcon(action) {
      const t = this.activityTypes.find(a => a.key === action)
      return t ? t.icon : '📌'
    },
    getActivityLabel(action) {
      const t = this.activityTypes.find(a => a.key === action)
      return t ? t.label : action
    },
    isOverdue(act) {
      if (!act.from_value || act.to_value === 'concluida') return false
      return new Date(act.from_value) < new Date()
    },
    formatDateTime(d) {
      if (!d) return ''
      return new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    },

    getInitials(nome) {
      if (!nome) return '?'
      return nome.trim().split(/\s+/).filter(w => w).slice(0, 2).map(n => n[0].toUpperCase()).join('')
    },

    getFieldIcon(label) {
      if (!label) return 'CircleIcon'
      const l = label.toLowerCase()
      if (l.includes('nome') || l.includes('name')) return 'UserIcon'
      if (l.includes('whatsapp') || l.includes('fone') || l.includes('telefone') || l.includes('celular')) return 'PhoneIcon'
      if (l.includes('mail') || l.includes('email')) return 'MailIcon'
      if (l.includes('mensagem') || l.includes('problema') || l.includes('duvida') || l.includes('pergunta')) return 'MessageCircleIcon'
      if (l.includes('empresa') || l.includes('company')) return 'BriefcaseIcon'
      return 'ChevronRightIcon'
    },

    sourceLabel(source) {
      const labels = { google: 'Google Ads', meta: 'Meta Ads', utm: 'Campanha', organic: 'Orgânico' }
      return labels[source] || 'Orgânico'
    },

    sourceIcon(source) {
      if (source === 'google') {
        return '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>'
      }
      if (source === 'meta') {
        return '<svg width="14" height="14" viewBox="0 0 24 24" fill="#1877F2" xmlns="http://www.w3.org/2000/svg"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>'
      }
      return '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>'
    },

    truncateUrl(url) {
      if (!url) return ''
      try {
        const u = new URL(url)
        return u.hostname.replace('www.', '') + (u.pathname !== '/' ? u.pathname.slice(0, 30) : '')
      } catch {
        return url.length > 40 ? url.slice(0, 40) + '…' : url
      }
    },

    formatDate(dt) {
      if (!dt) return '—'
      try {
        return new Date(dt).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
      } catch { return dt }
    },

    async salvarNota() {
      if (!this.novaNote.trim()) return
      this.savingNote = true
      try {
        const resp = await apiBack.post('lead-notes', {
          id_conversa: this.leadId,
          id_user: this.getUserData.id,
          note: this.novaNote.trim(),
        })
        if (!this.detalhe.notes) this.detalhe.notes = []
        this.detalhe.notes.unshift(resp.data)
        this.novaNote = ''
        this.$message({ type: 'success', message: 'Anotação salva!' })
      } catch (e) {
        console.error(e)
        this.$message({ type: 'error', message: 'Erro ao salvar anotação' })
      } finally {
        this.savingNote = false
      }
    },

    async togglePin(nota) {
      try {
        await apiBack.put(`lead-notes/${nota.id}`, { is_pinned: nota.is_pinned ? 0 : 1 })
        nota.is_pinned = nota.is_pinned ? 0 : 1
      } catch (e) { console.error(e) }
    },

    deletarNota(nota) {
      this.notaParaDeletar = nota
      this.$bvModal.show('modal-del-nota')
    },

    async confirmarDeleteNota() {
      if (!this.notaParaDeletar) return
      try {
        await apiBack.delete(`lead-notes/${this.notaParaDeletar.id}`)
        this.detalhe.notes = this.detalhe.notes.filter(n => n.id !== this.notaParaDeletar.id)
        this.$bvModal.hide('modal-del-nota')
        this.$message({ type: 'success', message: 'Anotação removida' })
      } catch (e) { console.error(e) }
    },
  },
}
</script>

<style lang="scss" scoped>
$dk-navy:   #0D3349;
$dk-orange: #E86A10;
$dk-gold:   #F5B935;
$dk-red:    #C12B2B;

.crm-lead-page {
  padding: 24px 32px;
  max-width: 1200px;
  margin: 0 auto;
  min-height: 80vh;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

// ── Topbar breadcrumb ─────────────────────────────────
.crm-topbar {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.82rem;
  color: var(--dk-text-muted, #9ca5b0);
}
.crm-topbar__sep { color: var(--dk-text-faint, #d0d7de); }
.crm-topbar__crumb { font-weight: 600; color: var(--dk-text-primary, $dk-navy); }

.crm-back-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: 1.5px solid var(--dk-border, #d0d7de);
  border-radius: 8px;
  padding: 5px 12px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--dk-text-primary, $dk-navy);
  cursor: pointer;
  transition: border-color .15s, color .15s;
  &:hover { border-color: $dk-orange; color: $dk-orange; }
}

// ── Loading / Empty ──────────────────────────────────
.crm-loading, .crm-empty {
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; gap: 12px; padding: 80px 0;
  color: var(--dk-text-muted, #9ca5b0);
}

// ── HERO ─────────────────────────────────────────────
.crm-hero {
  display: flex;
  align-items: center;
  gap: 20px;
  background: $dk-navy;
  border-radius: 18px;
  padding: 28px 32px;
  color: #fff;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50px; right: -50px;
    width: 200px; height: 200px;
    border-radius: 50%;
    background: rgba(#fff, 0.04);
    pointer-events: none;
  }
  &::after {
    content: '';
    position: absolute;
    bottom: -60px; right: 100px;
    width: 240px; height: 240px;
    border-radius: 50%;
    background: rgba($dk-orange, 0.07);
    pointer-events: none;
  }

  @media (max-width: 700px) { flex-wrap: wrap; }
}

.crm-hero__avatar-ring {
  width: 76px;
  height: 76px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 4px;
}

.crm-hero__avatar {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: $dk-orange;
  color: #fff;
  font-size: 1.4rem;
  font-weight: 800;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 0 0 4px rgba(#fff, .12);
  letter-spacing: .03em;
}

.crm-hero__info {
  flex: 1;
  min-width: 0;
}
.crm-hero__name {
  font-size: 1.45rem;
  font-weight: 800;
  color: #fff;
  margin: 0 0 6px;
  line-height: 1.2;
}
.crm-hero__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.crm-hero__meta-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.78rem;
  color: rgba(#fff, .7);
  background: rgba(#fff, .08);
  border-radius: 20px;
  padding: 3px 10px;
}

.crm-hero__badges {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  flex-shrink: 0;
  @media (max-width: 700px) { flex-direction: row; }
}

.crm-completion-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 20px;
  padding: 5px 12px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: .02em;
  border: 1px solid transparent;
  strong { font-size: 0.68rem; opacity: 0.95; }
  &--ok {
    background: rgba(16,185,129,.16);
    color: #34d399;
    border-color: rgba(16,185,129,.35);
  }
  &--pending {
    background: rgba(148,163,184,.18);
    color: #cbd5e1;
    border-color: rgba(148,163,184,.38);
  }
}

/* Pipeline Steps */
.crm-pipeline {
  display: flex;
  gap: 4px;
  margin-bottom: 24px;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 12px;
  padding: 6px;
  overflow-x: auto;
}
.crm-pipeline__step {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 10px 18px;
  border-radius: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s;
  flex: 1;
  justify-content: center;
  font-family: inherit;

  &:hover {
    background: rgba(255,255,255,0.07);
  }
  &--active {
    background: var(--stage-color) !important;
    .crm-pipeline__dot { background: #fff; border-color: #fff; }
    .crm-pipeline__label { color: #fff; font-weight: 700; }
  }
  &--done {
    .crm-pipeline__dot { background: var(--stage-color); border-color: var(--stage-color); }
    .crm-pipeline__label { color: var(--stage-color); }
  }
}
.crm-pipeline__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.25);
  background: transparent;
  flex-shrink: 0;
  transition: all 0.2s;
}
.crm-pipeline__label {
  font-size: 12.5px;
  font-weight: 600;
  color: #94a3b8;
  white-space: nowrap;
  transition: all 0.2s;
}

.crm-badge {
  display: inline-flex; align-items: center; gap: 4px;
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: .03em;

  &--success {
    background: rgba(#28c76f, .15);
    color: #28c76f;
  }
}

// ── Source badge ──────────────────────────────────────
.crm-source-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 20px;
  padding: 5px 13px;
  font-size: 0.73rem;
  font-weight: 700;
  letter-spacing: .03em;

  &--google  { background: rgba(#4285F4, .12); color: #4285F4; }
  &--meta    { background: rgba(#1877F2, .12); color: #1877F2; }
  &--utm     { background: rgba($dk-gold, .15); color: darken($dk-gold, 20%); }
  &--organic { background: rgba(#28c76f, .12); color: #28c76f; }

  &--lg { padding: 6px 16px; font-size: 0.78rem; }
}
.crm-source-badge__icon {
  display: flex;
  align-items: center;
  line-height: 1;
  svg { display: block; }
}

// ── BODY LAYOUT ───────────────────────────────────────
.crm-body {
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 20px;
  align-items: flex-start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
}

// ── CARDS ─────────────────────────────────────────────
.crm-card {
  background: var(--dk-bg-card, #fff);
  border: 1px solid var(--dk-border, #edf1f4);
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,.04);

  & + & { margin-top: 14px; }

  &--utm { border-top: 3px solid $dk-orange; }
}

.crm-card__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 13px 18px;
  background: var(--dk-bg-page, #f7f9fb);
  border-bottom: 1px solid var(--dk-border, #edf1f4);
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .07em;
  color: var(--dk-text-muted, #9ca5b0);
}
.crm-card__icon { color: $dk-orange; }

.crm-card__body { padding: 6px 0; }

// ── Info rows (sidebar contact) ───────────────────────
.crm-info-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 11px 18px;
  border-bottom: 1px solid var(--dk-border-light, #f3f6f8);
  &:last-child { border-bottom: none; }
}
.crm-info-row__icon {
  width: 32px; height: 32px;
  border-radius: 8px;
  background: rgba($dk-orange, .08);
  color: $dk-orange;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.crm-info-row__label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: var(--dk-text-faint, #b0bac4);
  margin-bottom: 2px;
}
.crm-info-row__value {
  font-size: 0.87rem;
  color: var(--dk-text-primary, $dk-navy);
  font-weight: 500;
  word-break: break-word;
  display: block;

  &--link {
    color: $dk-orange;
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
}
.crm-info-row__empty {
  padding: 14px 18px;
  font-size: 0.82rem;
  color: var(--dk-text-faint, #b0bac4);
}

// ── UTM rows ──────────────────────────────────────────
.crm-utm-source-wrap {
  padding: 14px 18px 10px;
}

.crm-utm-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 18px;
  border-bottom: 1px solid var(--dk-border-light, #f3f6f8);
  gap: 10px;

  &:last-child { border-bottom: none; }
}
.crm-utm-row__key {
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--dk-text-muted, #9ca5b0);
  text-transform: uppercase;
  letter-spacing: .05em;
  white-space: nowrap;
  flex-shrink: 0;
}
.crm-utm-row__val {
  font-size: 0.82rem;
  color: var(--dk-text-primary, $dk-navy);
  font-weight: 500;
  text-align: right;
  word-break: break-all;

  &--truncate {
    max-width: 130px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &--link {
    color: $dk-orange;
    text-decoration: none;
    word-break: break-all;
    &:hover { text-decoration: underline; }
  }
}
.crm-utm-empty {
  padding: 14px 18px;
  font-size: 0.8rem;
  color: var(--dk-text-muted, #9ca5b0);
  display: flex;
  align-items: center;
  gap: 6px;
}
.crm-utm-page {
  display: flex;
  justify-content: space-between;
  padding: 10px 18px;
  border-top: 1px dashed var(--dk-border, #edf1f4);
  margin-top: 4px;
  gap: 10px;
}

// ── Flow card ─────────────────────────────────────────
.crm-flow-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
}
.crm-flow-card__icon-wrap {
  width: 36px; height: 36px;
  border-radius: 9px;
  background: rgba($dk-orange, .08);
  color: $dk-orange;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.crm-flow-card__name {
  font-size: 0.87rem;
  font-weight: 600;
  color: var(--dk-text-primary, $dk-navy);
}

// ── TABS ──────────────────────────────────────────────
.crm-tabs {
  display: flex;
  gap: 4px;
  padding: 4px;
  background: var(--dk-bg-inset, #f3f6f9);
  border-radius: 12px;
  margin-bottom: 16px;
}

.crm-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border: none;
  border-radius: 9px;
  background: none;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--dk-text-muted, #9ca5b0);
  cursor: pointer;
  transition: background .15s, color .15s;

  &:hover { color: var(--dk-text-primary, $dk-navy); background: var(--dk-bg-card-hover, rgba(#fff, .6)); }

  &--active {
    background: var(--dk-bg-card, #fff);
    color: var(--dk-text-primary, $dk-navy);
    box-shadow: 0 1px 4px rgba(0,0,0,.07);
  }
}

.crm-tab__count {
  background: rgba($dk-orange, .12);
  color: $dk-orange;
  border-radius: 20px;
  padding: 1px 7px;
  font-size: 0.68rem;
  font-weight: 700;
}

.crm-tab-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.crm-key-answers {
  background: var(--dk-bg-card, #fff);
  border: 1px solid var(--dk-border, #edf1f4);
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,.04);
}
.crm-key-answers__header {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 12px 16px;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
  color: var(--dk-text-muted, #64748b);
  border-bottom: 1px solid var(--dk-border-light, #f3f6f8);
}
.crm-key-answers__count {
  margin-left: auto;
  background: rgba(232,106,16,0.12);
  color: #E86A10;
  border-radius: 999px;
  padding: 2px 9px;
  font-size: 0.68rem;
}
.crm-key-answers__list {
  display: flex;
  flex-direction: column;
}
.crm-key-answers__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid var(--dk-border-light, #f3f6f8);
  &:last-child { border-bottom: none; }
}
.crm-key-answers__item--ok {
  background: rgba(16,185,129,0.03);
}
.crm-key-answers__item--missing {
  background: rgba(148,163,184,0.05);
}
.crm-key-answers__label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #334155;
}
.crm-key-answers__value {
  font-size: 0.8rem;
  color: #0f172a;
  text-align: right;
  max-width: 65%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// ── Fields list ───────────────────────────────────────
.crm-fields-list {
  background: var(--dk-bg-card, #fff);
  border: 1px solid var(--dk-border, #edf1f4);
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,.04);
}

.crm-field-item {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 20px;
  border-bottom: 1px solid var(--dk-border-light, #f3f6f8);
  transition: background .12s;

  &:last-child { border-bottom: none; }
  &:hover { background: var(--dk-bg-card-hover, #f9fbfc); }
}
.crm-field-item__icon {
  width: 36px; height: 36px;
  border-radius: 9px;
  background: rgba($dk-orange, .08);
  color: $dk-orange;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.crm-field-item__label {
  font-size: 0.69rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: var(--dk-text-faint, #b0bac4);
  margin-bottom: 3px;
}
.crm-field-item__value {
  font-size: 0.92rem;
  color: var(--dk-text-primary, $dk-navy);
  font-weight: 500;
  word-break: break-word;
  line-height: 1.4;
}

// ── Notes ─────────────────────────────────────────────
.crm-note-form {
  background: var(--dk-bg-card, #fff);
  border: 1px solid var(--dk-border, #edf1f4);
  border-radius: 14px;
  padding: 16px 20px;
  box-shadow: 0 1px 4px rgba(0,0,0,.04);
  margin-bottom: 14px;
}
.crm-note-input {
  width: 100%;
  border: 1.5px solid var(--dk-border, #e4e8ed);
  border-radius: 10px;
  padding: 11px 14px;
  font-size: 0.87rem;
  background: var(--dk-bg-input, #fff);
  color: var(--dk-text-primary, $dk-navy);
  resize: vertical;
  min-height: 80px;
  transition: border-color .15s;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: $dk-orange;
  }
  &::placeholder { color: var(--dk-text-faint, #c0c8d2); }
}
.crm-note-form__footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}

.crm-notes-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.crm-note-item {
  background: var(--dk-bg-card, #fff);
  border: 1px solid var(--dk-border, #edf1f4);
  border-radius: 14px;
  padding: 16px 20px;
  box-shadow: 0 1px 4px rgba(0,0,0,.04);

  &--pinned {
    border-color: rgba($dk-orange, .3);
    background: rgba($dk-orange, .02);
  }
}
.crm-note-item__header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.crm-note-pin {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .05em;
  color: $dk-orange;
  background: rgba($dk-orange, .08);
  border-radius: 20px;
  padding: 2px 8px;
}
.crm-note-item__date {
  font-size: 0.75rem;
  color: var(--dk-text-faint, #b0bac4);
  margin-left: auto;
}
.crm-note-item__actions {
  display: flex;
  gap: 4px;
}
.crm-note-action {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px; height: 28px;
  border: 1px solid var(--dk-border, #edf1f4);
  border-radius: 7px;
  background: none;
  color: var(--dk-text-muted, #9ca5b0);
  cursor: pointer;
  transition: border-color .12s, color .12s;

  &:hover { border-color: $dk-orange; color: $dk-orange; }

  &--delete:hover { border-color: $dk-red; color: $dk-red; }
}
.crm-note-item__text {
  font-size: 0.88rem;
  color: var(--dk-text-primary, #444f5a);
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
}

// ── Buttons ───────────────────────────────────────────
.crm-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  border-radius: 10px;
  font-size: 0.83rem;
  font-weight: 700;
  cursor: pointer;
  border: none;
  transition: opacity .15s, transform .1s;

  &:active { transform: scale(0.98); }
  &:disabled { opacity: .5; cursor: not-allowed; }

  &--primary {
    background: $dk-orange;
    color: #fff;
    &:hover:not(:disabled) { opacity: .88; }
  }
  &--ghost {
    background: none;
    border: 1.5px solid var(--dk-border, #d0d7de);
    color: var(--dk-text-primary, $dk-navy);
    &:hover { border-color: $dk-orange; color: $dk-orange; }
  }
  &--danger {
    background: $dk-red;
    color: #fff;
    &:hover { opacity: .88; }
  }
}

// ── Empty state tab ───────────────────────────────────
.crm-tab-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 48px 24px;
  color: var(--dk-text-muted, #9ca5b0);
  font-size: 0.85rem;

  svg { color: var(--dk-text-faint, #d0d7de); }
  p { margin: 0; font-weight: 600; color: var(--dk-text-primary, $dk-navy); }
  span { font-size: 0.78rem; }
}

// ── Activities ──────────────────────────────────────────
.act-form {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 18px;
  margin-bottom: 20px;
}
.act-form__types {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
}
.act-type-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 8px;
  border: 1.5px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;
  &:hover { border-color: #cbd5e1; background: #f8fafc; }
  &--active { border-color: $dk-orange; background: #FFF7ED; }
}
.act-type-btn__icon { font-size: 14px; }
.act-type-btn__label { font-size: 12.5px; font-weight: 600; color: #334155; }
.act-form__row {
  display: flex;
  gap: 8px;
  align-items: center;
}
.act-form__input {
  flex: 1;
  padding: 10px 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  font-family: inherit;
  outline: none;
  &:focus { border-color: $dk-orange; }
}
.act-form__date {
  padding: 10px 12px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 12px;
  font-family: inherit;
  color: #475569;
  outline: none;
  &:focus { border-color: $dk-orange; }
}
.act-form__btn {
  display: flex;
  align-items: center;
  padding: 10px 18px;
  border-radius: 8px;
  border: none;
  background: $dk-orange;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  font-family: inherit;
  transition: opacity 0.18s;
  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
}

.act-section { margin-bottom: 24px; }
.act-section__title {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 700;
  color: #334155;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  &--done { color: #10B981; }
  &--hist { color: #64748b; }
}
.act-section__count {
  font-size: 11px;
  background: #f1f5f9;
  color: #64748b;
  padding: 1px 8px;
  border-radius: 6px;
  margin-left: 8px;
  font-weight: 700;
}

.act-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  margin-bottom: 8px;
  transition: all 0.15s;
  &:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.04); }
  &--overdue { border-color: #FECACA; background: #FFF5F5; }
  &--done { opacity: 0.65; }
}
.act-card__check {
  border: none;
  background: none;
  color: #cbd5e1;
  cursor: pointer;
  padding: 2px;
  flex-shrink: 0;
  transition: color 0.15s;
  &:hover { color: #10B981; }
  &--done { color: #10B981; cursor: default; }
}
.act-card__body { flex: 1; min-width: 0; }
.act-card__top {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.act-card__type-icon { font-size: 14px; }
.act-card__type-label { font-size: 13px; font-weight: 700; color: #1e293b; }
.act-card__date { font-size: 11.5px; color: #64748b; display: flex; align-items: center; }
.act-card__overdue-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 4px;
  background: #FEE2E2;
  color: #DC2626;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.act-card__detail { font-size: 13px; color: #475569; margin: 4px 0 0; line-height: 1.5; }
.act-card__delete {
  border: none;
  background: none;
  color: #cbd5e1;
  cursor: pointer;
  padding: 2px;
  flex-shrink: 0;
  &:hover { color: #EF4444; }
}

// Timeline history
.act-hist-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
  color: #64748b;
  &:last-child { border-bottom: none; }
}
.act-hist-item__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #cbd5e1;
  flex-shrink: 0;
}
.act-hist-item__text { flex: 1; }
.act-hist-item__text strong { color: #334155; }
.act-hist-item__date { font-size: 11px; color: #94a3b8; white-space: nowrap; }

// ── Delete modal ──────────────────────────────────────
.crm-delete-modal {
  text-align: center;
  padding: 28px 24px;

  &__header {
    width: 52px; height: 52px;
    border-radius: 50%;
    background: rgba($dk-red, .1);
    color: $dk-red;
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 16px;
  }
  h4 { font-size: 1rem; font-weight: 700; color: var(--dk-text-primary, $dk-navy); margin-bottom: 6px; }
  p  { font-size: 0.85rem; color: var(--dk-text-secondary, #6b7a8d); }

  &__footer {
    display: flex;
    gap: 10px;
    justify-content: center;
    margin-top: 20px;
  }
}
</style>
